import { Classes, Tag, TagProps } from "@blueprintjs/core";
import { lighten, rgba } from "polished";
import React from "react";
import styled, { css } from "styled-components";

import { STATIC_ICON } from "../components/icons/staticIcons";

export const tagStyles = css<{ $small?: boolean }>`
  &.${Classes.TAG}.${Classes.MINIMAL} {
    color: ${({ theme }) => theme.fontColor.DEFAULT};

    background-color: ${({ theme }) =>
      rgba(lighten(0.1, theme.fontColor.DEFAULT), 0.08)};

    &.${Classes.INTENT_PRIMARY} {
      color: ${({ theme }) => theme.intent.PRIMARY};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.PRIMARY), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.PRIMARY};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.PRIMARY, 0.7)};
      }
    }

    &.${Classes.INTENT_SUCCESS} {
      color: ${({ theme }) => theme.intent.SUCCESS};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.SUCCESS), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.SUCCESS};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.SUCCESS, 0.7)};
      }
    }

    &.${Classes.INTENT_WARNING} {
      color: ${({ theme }) => theme.intent.WARNING};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.WARNING), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.WARNING};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.WARNING, 0.7)};
      }
    }

    &.${Classes.INTENT_DANGER} {
      color: ${({ theme }) => theme.intent.DANGER};

      background-color: ${({ theme }) =>
        rgba(lighten(0.1, theme.intent.DANGER), 0.12)};

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.intent.DANGER};
      }

      .${Classes.TAG_REMOVE} > .${Classes.ICON} {
        background-color: ${({ theme }) => rgba(theme.intent.DANGER, 0.7)};
      }
    }

    ${({ $small }) =>
      $small &&
      css`
        font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
        line-height: 16px;
        height: 16px;
        min-height: 16px;
        padding: 0px 4px;
      `}
  }

  &.${Classes.TAG} > .${Classes.TAG_REMOVE} {
    .${Classes.ICON} {
      background-color: ${({ theme }) => theme.fontColor.MUTED};
      background-image: none;
      mask-image: ${`url('data:image/svg+xml,${STATIC_ICON.CrossSmall}')`};
      svg {
        visibility: hidden;
      }
    }
  }
`;

const StyledTag = styled(Tag)<{ $small?: boolean }>`
  ${tagStyles}
`;

/**
 * https://blueprintjs.com/docs/#core/components/tag
 */

interface HexTagUniqueProps {
  small?: boolean;
  /**
   * Blueprint uses the `title` prop to display a default tooltip.
   * Use disableTooltip if you want to wrap the tag into a HexTooltip, and do not want duplicate tooltips on the HexTag.
   */
  disableTooltip?: boolean;
}

export interface HexTagProps extends TagProps, HexTagUniqueProps {}

export const HexTag: React.ComponentType<HexTagProps> = ({
  disableTooltip,
  small,
  ...props
}) => (
  <StyledTag
    {...props}
    $small={small}
    htmlTitle={disableTooltip ? undefined : props.title}
    minimal={true}
  />
);
